import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import React from "react";
import { EMPLOYEE_TEMP_LABEL } from "../../util/constants.js";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import CustomLoadingTableOverlay from "../CustomLoadingTableOverlay.jsx";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay.jsx";
import { Button, Chip } from "@mui/material";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { useSelector } from "react-redux";
import { VOLUNTARY_OT_PAGE } from "../../util/overtime/constants.js";

const useStyles = makeStyles((theme) => ({
  infoButton: {
    margin: "2px",
    width: "120px",
    minWidth: "120px",
  },
  otListOwnItem: {
    background: theme.palette.secondary.swaLightestBlue,
  },
  redStatusColor: {
    color: theme.palette.primary.swaRed,
  },
}));

const VoluntaryOTListTable = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);

  const renderDeadlineCell = (otRequest) => {
    return (
      <div className="deadline-cell">
        {otRequest.isBeforeDeadline ? <p>Above</p> : <p className={classes.redStatusColor}>Below</p>}
      </div>
    );
  };

  const renderShiftCell = (otRequest) => {
    return (
      <div className="shift-cell">
        {otRequest.isOff ? (
          <p>OFF</p>
        ) : (
          <p style={{ whiteSpace: "normal", textAlign: "left" }}>
            {otRequest.shifts.map((shift) => shift.start + "-" + shift.end).join(", ")}
          </p>
        )}
      </div>
    );
  };

  const renderMoreInfoCell = (otRequest) => {
    return (
      <Button
        className={classes.infoButton}
        variant="contained"
        color="primary"
        onClick={() => props.setOtRequestSelectedForMoreInfo(otRequest)}
      >
        More Info
      </Button>
    );
  };

  const renderEmployee = (otRequest) => {
    return (
      <>
        {otRequest.lastName}, {otRequest.firstName} ({otRequest.eid})
        {otRequest.isTemp && <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />}
      </>
    );
  };

  const getRowClassName = (params) => {
    if (props.isDesktopView) {
      if (params.row.eid === userSession.eid) {
        return classes.otListOwnItem;
      }
    }
  };

  const renderVoluntaryOTTable = () => {
    const columns = [
      {
        field: "isBeforeDeadline",
        headerName: "Deadline",
        type: "string",
        flex: 0.1,
        renderCell: (params) => {
          return renderDeadlineCell(params.row);
        },
        sortable: false,
      },
      {
        field: "employee",
        valueGetter: (params) => {
          return params.row;
        },
        renderCell: (params) => {
          return renderEmployee(params.row);
        },
        headerName: "Name",
        type: "string",
        flex: 0.2,
        sortable: false,
      },
      {
        field: "shifts",
        headerName: "Scheduled Hours",
        type: "string",
        flex: 0.3,
        valueGetter: (params) => {
          return params.row;
        },
        renderCell: (params) => {
          return renderShiftCell(params.row);
        },
        sortable: false,
      },
      {
        field: "seniorityDate",
        headerName: "Classification Seniority Date",
        type: "string",
        flex: 0.15,
        sortable: false,
      },
      {
        field: "otAssignment",
        headerName: "OT Assignment",
        type: "string",
        flex: 0.3,
        sortable: false,
      },
      {
        field: "moreInfo",
        headerName: "",
        type: "string",
        flex: 0.17,
        sortable: false,
        renderCell: (params) => {
          return renderMoreInfoCell(params.row);
        },
      },
      {
        field: "station",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return value.station;
        },
        type: "string",
        headerName: "Station",
        hide: true,
      },
      {
        field: "referenceNumber",
        headerName: "Reference #",
        type: "string",
        hide: true,
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", hide: true },
      {
        field: "employeeId",
        headerName: "EE ID",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return value.empid;
        },
        type: "string",
        hide: true,
      },
    ];

    return (
      <div style={{ width: "100%", height: "inherit" }}>
        <DataGridPro
          apiRef={props.otTableDesktopRef}
          getRowClassName={getRowClassName}
          rows={props.otRequests}
          getRowId={(row) => row.eventId}
          columns={columns}
          disableColumnMenu={true}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          hideFooterPagination={true}
          getRowHeight={() => "auto"}
          components={{
            LoadingOverlay: CustomLoadingTableOverlay,
            NoRowsOverlay: CustomNoRowsTableOverlay,
            ErrorOverlay: CustomErrorTableOverlay,
          }}
          componentsProps={{
            noRowsOverlay: {
              pageType: VOLUNTARY_OT_PAGE,
            },
            errorOverlay: {
              errorMessage: props.errorObject.message,
              errorCode: props.errorObject.code,
            },
          }}
          loading={props.isLoading}
          error={props.isError ? true : undefined}
        />
      </div>
    );
  };

  return <div className="stars-table">{renderVoluntaryOTTable()}</div>;
};

VoluntaryOTListTable.propTypes = {
  otRequests: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  otTableDesktopRef: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  setOtRequestSelectedForMoreInfo: PropTypes.func.isRequired,
};

export default VoluntaryOTListTable;
